import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, Download, Eye, Trash2, User, Mail, Phone, Globe, ListPlus,ShieldOff  } from 'lucide-react';
import UserModal from '../UserModal/UserModal';
import './UserDetails.css';

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
const MySwal = withReactContent(Swal);

// --- Define the URLs for BOTH of your backend services ---
const USER_MICROSERVICE_URL = process.env.REACT_APP_LOGIN_APP_API_BASE;
const ADMIN_BACKEND_URL = process.env.REACT_APP_API_URL ;
const appName = process.env.REACT_APP_TARGET_APP_NAME;



const AttractiveLoader = () => (
  <div className="ud-attractive-loader-overlay">
    <div className="ud-attractive-loader">
      <div className="ud-loader-dot"></div>
      <div className="ud-loader-dot"></div>
      <div className="ud-loader-dot"></div>
    </div>
  </div>
);

const UserDetails = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);

  // State for "Add to List" functionality
  const [emailLists, setEmailLists] = useState([]);
  const [isAddToListModalOpen, setIsAddToListModalOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userToAddToList, setUserToAddToList] = useState(null);
  const [selectedListId, setSelectedListId] = useState('');

  // --- Refactored to a single, clean data fetching function ---
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setError(null);

       // --- DEBUGGING: Log the URLs to the console ---
      const usersURL = `${USER_MICROSERVICE_URL}/api/users?appName=${appName}&limit=10000`;
      const listsURL = `${ADMIN_BACKEND_URL}/api/lists/names`;
      console.log("Attempting to fetch from:");
      console.log("Users URL:", usersURL);
      console.log("Lists URL:", listsURL);
      // --- END DEBUGGING ---

      try {
        const token = localStorage.getItem('adminToken');
        if (!token) throw new Error('Authentication token not found.');

        // --- Fetch data from two different servers in parallel ---
        const [usersResponse, listsResponse] = await Promise.all([
          // 1. Fetch users from the microservice
          fetch(`${USER_MICROSERVICE_URL}/api/users?appName=${appName}&limit=10000`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          // 2. Fetch email lists from the main admin backend
          fetch(`${ADMIN_BACKEND_URL}/api/lists/names`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        if (!usersResponse.ok) throw new Error(`Failed to fetch users (Status: ${usersResponse.status})`);
        if (!listsResponse.ok) throw new Error(`Failed to fetch email lists (Status: ${listsResponse.status})`);

        const usersData = await usersResponse.json();
        const listsData = await listsResponse.json();

        // --- Process the user data on arrival to add a 'fullName' property ---
        const processedUsers = (usersData.users || []).map(user => ({
          ...user,
          fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim()
        }));

        setUsers(processedUsers);
        setEmailLists(listsData);

      } catch (err) {
        console.error("Error fetching initial data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // --- Helper Functions and Filtering Logic ---
  const getUserLatestReport = (user) => {
    if (!user?.reports?.length) return null;
    return [...user.reports].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  
  const formatLastLogin = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    return formatDate(dateString);
  };

  const filteredUsers = users.filter(user => {
    const latestReport = getUserLatestReport(user);
    const searchTermLower = searchTerm.toLowerCase();
    
    const matchesSearch =
      user.fullName?.toLowerCase().includes(searchTermLower) ||
      user.email?.toLowerCase().includes(searchTermLower) ||
      user.nationality?.toLowerCase().includes(searchTermLower) ||
      latestReport?.preferredCourse?.toLowerCase().includes(searchTermLower);

    let matchesFilter = true;
    if (filterType === 'active') matchesFilter = user.isActive === true;
    else if (filterType === 'inactive') matchesFilter = user.isActive === false;
    else if (filterType === 'verified') matchesFilter = user.isVerified === true;
    else if (filterType === 'unverified') matchesFilter = user.isVerified === false;
    
    return matchesSearch && matchesFilter;
  });

  const exportToCSV = () => {
    try {
      const csvContent = [
        ['UserID', 'FullName', 'Email', 'Phone', 'Nationality', 'Status', 'Verified', 'LastLogin', 'JoinedDate'],
        ...filteredUsers.map(user => [
          user.userId,
          user.fullName,
          user.email,
          user.phone,
          user.nationality || '',
          user.isActive ? 'Active' : 'Inactive',
          user.isVerified ? 'Yes' : 'No',
          user.lastLogin ? new Date(user.lastLogin).toISOString() : 'Never',
          new Date(user.createdAt).toISOString()
        ])
      ].map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      MySwal.fire('Error', 'Failed to export CSV. Please try again.', 'error');
    }
  };

  const handleViewUser = (user) => {setIsModalLoading(true); setSelectedUser(user); setShowUserModal(true); };
  const handleCloseModal = () => { setShowUserModal(false); setSelectedUser(null); setIsModalLoading(false); };

  // --- NEW: Ban/Unban User Handler ---
 const handleBanUser = async (userToBan) => {
    const action = userToBan.isBanned ? 'Unban' : 'Ban';
    MySwal.fire({
      title: `Confirm ${action}`,
      text: `Are you sure you want to ${action.toLowerCase()} "${userToBan.fullName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: `Yes, ${action.toLowerCase()} user!`
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem('adminToken');

          // ✅ THE CORRECTED FETCH CALL
          // This will now build the correct URL: https://api-login.indraq.com/api/user/USERID006/ban
          const response = await fetch(`${USER_MICROSERVICE_URL}/api/user/${userToBan.userId}/ban`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}` }
          });

          const data = await response.json();
          if (!response.ok) throw new Error(data.message || 'Failed to update user status.');

          setUsers(prevUsers => prevUsers.map(user => 
            user.userId === userToBan.userId ? { ...user, isBanned: !user.isBanned } : user
          ));
          MySwal.fire('Success!', `User has been ${action.toLowerCase()}ned.`, 'success');
        } catch (error) {
          MySwal.fire('Error!', error.message, 'error');
        }
      }
    });
  };

 const handleDeleteUser = async (userToDelete) => {
    MySwal.fire({
      title: 'Are you absolutely sure?',
      html: `This will permanently delete "<b>${userToDelete.fullName}</b>" and all their associated data (payments, reports, sessions).<br/><b>This action cannot be undone.</b>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete everything!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem('adminToken');
          // This single API call will now trigger the full cascade delete on the backend
          const response = await fetch(`${USER_MICROSERVICE_URL}/api/user/${userToDelete.userId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to delete user.');
          }

          setUsers(prev => prev.filter(user => user.userId !== userToDelete.userId));
          MySwal.fire('Deleted!', 'The user and all their data have been deleted.', 'success');
        } catch (error) {
          MySwal.fire('Error!', error.message, 'error');
        }
      }
    });
  };
  
  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.userId));
    }
  };

  const openAddToListModal = (user = null) => {
    setUserToAddToList(user);
    setIsAddToListModalOpen(true);
  };

  const closeAddToListModal = () => {
    setIsAddToListModalOpen(false);
    setUserToAddToList(null);
    setSelectedListId('');
  };

   const handleConfirmAddToList = async (e) => {
    e.preventDefault();
    if (!selectedListId) {
        MySwal.fire('Incomplete', 'Please select a list.', 'info');
        return;
    }

    const usersToAdd = userToAddToList
      ? [{ userId: userToAddToList.userId, name: userToAddToList.fullName, email: userToAddToList.email }]
      : users.filter(u => selectedUsers.includes(u.userId)).map(u => ({ userId: u.userId, name: u.fullName, email: u.email }));
      
    if (usersToAdd.length === 0) {
        MySwal.fire('No Selection', 'No users were selected to add to the list.', 'warning');
        return;
    }

    try {
      // This correctly targets the admin backend to add users to a list
      const response = await fetch(`${ADMIN_BACKEND_URL}/api/lists/${selectedListId}/users/add`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({ users: usersToAdd }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to add users.');
      
      let successMessage = data.message + (data.duplicates ? `\n${data.duplicates}` : '');
      MySwal.fire('Success!', successMessage, 'success');
      
      closeAddToListModal();
      setSelectedUsers([]);
    } catch (error) {
      MySwal.fire('Error!', error.message, 'error');
    }
  };
  
  return (
    <div className="ud-user-details">
      <Helmet>
        <title>User Management | GN VISA Admin</title>
        <meta name="description" content="View, search, filter, and manage all registered users of the application." />
      </Helmet>
      <div className="ud-user-details-header">
        {/* <div className="ud-header-title">
          <h2>User Management</h2>
          <p>Manage registered users and their account information</p>
        </div> */}
        <div className="ud-search-filter-section">
          <div className="ud-search-box">
            <input type="text" placeholder="Search by name, email, country..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="ud-search-input" />
            <Search size={16} className="ud-search-icon" />
          </div>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="ud-filter-select">
            <option value="all">All Users</option>
            <option value="active">Active</option>
            <option value="banned">Banned Users</option>
            <option value="inactive">Inactive</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
          </select>
          <button onClick={exportToCSV} className="ud-export-button" disabled={filteredUsers.length === 0}>
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      {selectedUsers.length > 0 && (
        <div className="ud-bulk-action-bar">
          <span>{selectedUsers.length} user(s) selected</span>
          <button onClick={() => openAddToListModal()} className="ud-bulk-action-button">
            <ListPlus size={16} /> Add to List
          </button>
        </div>
      )}

      <div className="ud-users-table-container">
        <div className="ud-users-table">
          <div className="ud-table-header">
            <div className="ud-table-header-row">
              <div className="ud-checkbox-cell">
                <input type="checkbox" onChange={handleSelectAllUsers} checked={filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length} title="Select all" />
              </div>
              <div>S.No.</div>
              <div>User</div>
              <div>Contact & Country</div>
              <div>Last Login</div>
              <div>Actions</div>
            </div>
          </div>
          <div className="ud-table-body">
            {loading ? (
              <div className="ud-loading-message"><div className="ud-loading-spinner"></div>Loading users...</div>
            ) : error ? (
                <div className="ud-no-users-message"><User size={48} color="#ef4444" /><h3>Error Loading Users</h3><p>{error}</p></div>
            ) : filteredUsers.length === 0 ? (
              <div className="ud-no-users-message"><User size={48} color="#e2e8f0" /><h3>No users found</h3><p>No users match your current search and filter criteria.</p></div>
            ) : (
              filteredUsers.map((user, index) => {
                const isSelected = selectedUsers.includes(user.userId);
                const rowClass = `ud-table-row ${isSelected ? 'ud-row-selected' : ''} ${user.isBanned ? 'ud-row-banned' : ''}`;
                return (
                  <div key={user.userId} className={`ud-table-row ${isSelected ? 'ud-row-selected' : ''}`}>
                    <div className="ud-checkbox-cell"><input type="checkbox" checked={isSelected} onChange={() => handleSelectUser(user.userId)} /></div>
                    <div className="ud-sno-cell">{index + 1}</div>
                    <div className="ud-user-cell">
                      <div className="ud-user-avatar">{user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}</div>
                      <div className="ud-user-info">
                        <div className="ud-user-name">{user.fullName || 'N/A'}</div>
                        <div className="ud-user-id">ID: {user.userId}</div>
                      </div>
                    </div>
                    <div className="ud-contact-cell">
                      <div className="ud-contact-item"><Mail size={14} /><span>{user.email || 'N/A'}</span></div>
                      <div className="ud-contact-item"><Phone size={14} /><span>{user.phone || 'N/A'}</span></div>
                      {/* <div className="ud-contact-item"><Globe size={14} /><span>{user.nationality || 'N/A'}</span></div> */}
                    </div>
                    <div className="ud-login-cell">
                      <div className="ud-last-login">{formatLastLogin(user.lastLogin)}</div>
                      <div className="ud-join-date">Joined {formatDate(user.createdAt)}</div>
                    </div>
                    <div className="ud-actions-cell">
                      <button className="ud-action-button ud-add-to-list-button" onClick={() => openAddToListModal(user)} title="Add to List"><ListPlus size={14} /></button>
                      <button className="ud-action-button ud-view-button" onClick={() => handleViewUser(user)} title="View Details"><Eye size={14} /></button>
                      <button className="ud-action-button ud-delete-button" onClick={() => handleDeleteUser(user)} title="Delete User"><Trash2 size={14} /></button>
                      <button 
                        className={`ud-action-button ${user.isBanned ? 'ud-unban-button' : 'ud-ban-button'}`}
                        onClick={() => handleBanUser(user)} 
                        title={user.isBanned ? 'Unban User' : 'Ban User'}
                      >
                        <ShieldOff size={14} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {isModalLoading && <AttractiveLoader />}

      {selectedUser && (
        <UserModal 
          user={selectedUser} 
          onClose={handleCloseModal}
          // Pass the state setter function directly to the child
          setIsLoading={setIsModalLoading} 
        />
      )}


      {isAddToListModalOpen && (
        <div className="ud-modal-overlay">
          <div className="ud-modal-content">
            <h2>Add User(s) to List</h2>
            <p>{userToAddToList ? `Select a list to add "${userToAddToList.fullName}" to.` : `Select a list to add the ${selectedUsers.length} selected users to.`}</p>
            <form onSubmit={handleConfirmAddToList}>
              <select value={selectedListId} onChange={(e) => setSelectedListId(e.target.value)} className="ud-modal-select" required>
                <option value="" disabled>-- Select an email list --</option>
                {emailLists.map(list => (<option key={list._id} value={list._id}>{list.name}</option>))}
              </select>
              <div className="ud-modal-actions">
                <button type="button" onClick={closeAddToListModal} className="ud-btn-cancel">Cancel</button>
                <button type="submit" className="ud-btn-confirm" disabled={!selectedListId}>Add to List</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetails;
